// medplum-history.js — ONLINE-PRIMARY patient longitudinal-history read path.
// The browser reads a patient's WHOLE visit graph DIRECTLY from Medplum
// (RLS-scoped by the doctor's own token via the shared getMedplum() session) and
// shapes it CLIENT-SIDE into the exact Visit[] DTO the PatientTimelineDrawer
// (patient-timeline.jsx) renders — the online-primary replacement for the Express
// route GET /api/v1/opd/view/patients/:patientId/history (which 401s under main-2
// because the browser no longer carries the Express staff session).
//
// ONE round trip for the whole timeline: a single
//   Encounter?subject=Patient/<uuid>&_sort=-date
//     &_revinclude=Composition:encounter
//     &_revinclude=Condition:encounter
//     &_revinclude=MedicationRequest:encounter
//     &_revinclude=ServiceRequest:encounter
//     &_revinclude=DocumentReference:encounter
// search. Every child references its Encounter (one hop from the Encounter
// results), so the _revinclude fans in every visit's children in ONE bundle —
// collapsing the server's per-encounter fan-out (6 searches × 20 visits) into a
// single call, per the mapper's "collapse to a single _revinclude" note (verified
// against the Medplum includes docs: multiple _revinclude params + the one-hop
// child→Encounter join are both supported; DocumentReference:encounter maps to
// DocumentReference.context.encounter, which the seed stamps).
//
// SCOPED TO THE ACCESS POLICY (same constraint as medplum-encounter.js): the demo
// `opd-doctor-rowlevel` AccessPolicy covers Composition/MedicationRequest/
// Observation/DocumentReference/ServiceRequest but NOT QuestionnaireResponse.
// Under Medplum RLS, _revinclude'ing a type the policy doesn't grant 403s the
// WHOLE search. So the five policy-covered types ride the main graph search and
// QuestionnaireResponse is fetched best-effort per-encounter afterwards (only the
// chief-complaint derivation needs it, and only when a visit has no signed/draft
// consult AND no chief_complaint Composition section — rare). When the policy
// gains a QR grant, add QuestionnaireResponse:encounter to REVINCLUDES.
//
// ID-SPACE (the mapper's critical gotcha): patientId MUST be a Medplum Patient
// UUID. The converted callers (Patients / Reception / Messaging) pass the real
// UUID; the still-Express Schedule caller can pass an OPD disk slug. A slug can
// never resolve a `subject=Patient/<uuid>` search — it would silently return zero
// visits and render a wrong "No encounters" empty state. So we FAIL LOUD on a
// non-UUID id with a clear message rather than issue a dangling search (redesign
// invariant: fail loud, no speculative fallback).
import { getMedplum } from './client-port.js';
import { chunk } from './medplum-bounded-search.js';
import { shapePatientHistory } from '@healther/clinical-core/shape/patient-history.js';

// Policy-covered child types that ride the ONE graph search. QuestionnaireResponse
// is deliberately absent (see header) and side-fetched best-effort.
const REVINCLUDES = [
  'Composition:encounter',
  'Condition:encounter',
  'MedicationRequest:encounter',
  'ServiceRequest:encounter',
  'DocumentReference:encounter',
];

// Canonical Medplum resource id = a UUID. The OPD disk slug is `opd-<uuid>` — it
// carries the extra `opd-` prefix and is NOT a valid Patient id to search by.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// A child resource's owning-encounter reference ('Encounter/<uuid>'):
// Composition/Condition/MedicationRequest/ServiceRequest use `.encounter`;
// DocumentReference uses `.context.encounter[0]`.
function encounterRefOf(r) {
  if (!r) return null;
  if (r.encounter && r.encounter.reference) return r.encounter.reference;
  const ctx = r.context && r.context.encounter && r.context.encounter[0];
  return (ctx && ctx.reference) || null;
}

// Group ONE Encounter?subject=…&_revinclude=… bundle into the per-encounter child
// sets shapePatientHistory expects. Encounters are collected newest-first (the
// _sort=-date order the search returns); each child is bucketed under its owning
// encounter's id.
function groupPatientGraph(bundle) {
  const encounters = [];
  const childrenByEncounter = new Map();
  const kidsFor = (encId) => {
    let k = childrenByEncounter.get(encId);
    if (!k) {
      k = { compositions: [], conditions: [], medicationRequests: [], serviceRequests: [], questionnaireResponses: [], documentReferences: [] };
      childrenByEncounter.set(encId, k);
    }
    return k;
  };
  const bucket = (r, key) => {
    const ref = encounterRefOf(r);
    const encId = ref ? ref.split('/')[1] : null;
    if (!encId) return;
    kidsFor(encId)[key].push(r);
  };
  for (const entry of (bundle && bundle.entry) || []) {
    const r = entry.resource; if (!r) continue;
    switch (r.resourceType) {
      case 'Encounter': encounters.push(r); break;
      case 'Composition': bucket(r, 'compositions'); break;
      case 'Condition': bucket(r, 'conditions'); break;
      case 'MedicationRequest': bucket(r, 'medicationRequests'); break;
      case 'ServiceRequest': bucket(r, 'serviceRequests'); break;
      case 'DocumentReference': bucket(r, 'documentReferences'); break;
      default: break;
    }
  }
  return { encounters, childrenByEncounter };
}

// Loads + shapes a patient's whole visit history. `patientId` is a Medplum
// Patient UUID (the converted callers pass this). `excludeEncounterId` drops the
// current encounter (used by the in-consult "prior visits" view; the drawer
// leaves it null so all visits show). `limit` caps the list (default 20, matching
// the server builder). Returns { returning, visits } — the drawer reads j.visits.
export async function loadPatientHistoryFromMedplum(patientId, { excludeEncounterId = null, limit = 20 } = {}) {
  const id = String(patientId || '').trim();
  if (!id) throw new Error('patientId is required to load history');
  // FAIL LOUD on a non-UUID id (an OPD disk slug from an unconverted caller):
  // a `subject=Patient/<slug>` search silently returns nothing, which would
  // render a false "No encounters on file" rather than the real error.
  if (!UUID_RE.test(id)) {
    throw new Error(`patient history needs a Medplum Patient UUID, got "${id}" (an OPD slug cannot resolve a patient-wide FHIR search)`);
  }

  const { medplum } = await getMedplum();

  // MedplumClient caches reads for 60s; invalidate first so a re-open (e.g. after
  // break-glass grants access) re-fetches instead of serving a stale/denied read.
  if (typeof medplum.invalidateSearches === 'function') {
    try { medplum.invalidateSearches('Encounter'); } catch { /* best-effort */ }
  }

  // FHIR-DIRECT read (no Bot): the patient's encounters + every policy-covered
  // child in ONE call, RLS-scoped to this doctor by their own token. A 403 here
  // (cross-department chart, no grant) propagates to the drawer, which shows its
  // calm "request access / break-glass" state — see patient-timeline.jsx.
  const query = `subject=Patient/${encodeURIComponent(id)}&_count=200&_sort=-date`
    + REVINCLUDES.map((r) => `&_revinclude=${r}`).join('');
  const bundle = await medplum.search('Encounter', query);

  const graph = groupPatientGraph(bundle);
  // Cap to `limit` visits BEFORE the QR side-fetch so we never over-fetch.
  graph.encounters = graph.encounters.slice(0, excludeEncounterId ? limit + 1 : limit);
  graph.excludeEncounterId = excludeEncounterId;

  // QuestionnaireResponse (intake chief-complaint source) is NOT in the demo
  // AccessPolicy, so it can't ride the main _revinclude (that 403s the whole
  // graph). Side-fetch it in OR-batched encounter= chunks, best-effort: JUSTIFIED,
  // not speculative — a policy-forbidden type is a real, expected state, and the
  // shaper already derives chief complaint from the Composition/QR chain and
  // degrades cleanly when QR is absent. Swallow ONLY 403; rethrow anything else.
  const needQrEncIds = [];
  for (const enc of graph.encounters) {
    const kids = graph.childrenByEncounter.get(enc.id);
    const hasIntakeComp = (kids && kids.compositions || []).some((c) =>
      (c._healther && c._healther.kind === 'intake-summary')
      || (c.type && c.type.coding && c.type.coding[0] && c.type.coding[0].code === 'intake-summary')
      || (c.type && c.type.text === 'intake-summary'));
    if (!hasIntakeComp) needQrEncIds.push(enc.id);
  }

  const QR_CHUNK = 20;
  for (const batch of chunk(needQrEncIds, QR_CHUNK)) {
    if (!batch.length) continue;
    const encParam = batch.map((id) => `Encounter/${id}`).join(',');
    try {
      const b = await medplum.search('QuestionnaireResponse', `encounter=${encParam}&_count=200`);
      for (const r of ((b && b.entry) || []).map((e) => e.resource).filter(Boolean)) {
        const ref = r.encounter && r.encounter.reference;
        const encId = ref ? ref.split('/')[1] : null;
        if (!encId) continue;
        const kids = graph.childrenByEncounter.get(encId);
        if (kids) kids.questionnaireResponses.push(r);
      }
    } catch (e) {
      if (e && (e.status === 403 || /forbidden/i.test((e && e.message) || ''))) continue;
      throw e;
    }
  }

  const visits = shapePatientHistory(graph);
  return { returning: visits.length > 0, visits };
}
